import {
  addMinutesToTimeHhMm,
  parseTimeToDate,
  toTimeHHmmss,
} from "@/shared/lib/date-time";
import {
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
  nearestSlotDurationMinutes,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import {
  formatQuarterHhMm,
  isCanonicalQuarterHhMm,
  parseQuarterClock,
} from "@/shared/lib/quarter-time";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormNumber,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { differenceInMinutes, format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import type { BookingSlot } from "../../domain/contracts/public-booking";
import { publicBookingApi } from "../api/client";

function toTimeHHmm5(s: string | undefined): string {
  const t = String(s ?? "").trim();
  if (!t) return "09:00";
  if (t.length >= 8) return t.slice(0, 5);
  return t;
}

function toQuarterHhMmInitial(s: string | undefined): string {
  const { h, m } = parseQuarterClock(toTimeHHmm5(s));
  return formatQuarterHhMm(h, m);
}

function inferSlotDurationMinutesFromRange(
  start?: string,
  end?: string,
): PrivateLessonSlotDurationMinutes {
  if (!start?.trim() || !end?.trim()) return 60;
  try {
    const mins = differenceInMinutes(
      parseTimeToDate(toQuarterHhMmInitial(end)),
      parseTimeToDate(toQuarterHhMmInitial(start)),
    );
    if (mins <= 0) return 60;
    if (PRIVATE_LESSON_SLOT_DURATION_SET.has(mins)) {
      return mins as PrivateLessonSlotDurationMinutes;
    }
    return nearestSlotDurationMinutes(mins);
  } catch {
    return 60;
  }
}

const slotDurZ = z.union([
  z.literal(15),
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(75),
  z.literal(90),
  z.literal(105),
  z.literal(120),
  z.literal(135),
  z.literal(150),
  z.literal(165),
  z.literal(180),
]);

const PublicRoomBookingSchema = z
  .object({
    startTime: z.string().min(1, "ກະລຸນາເລືອກເວລາເລີ່ມຕົ້ນ"),
    slotDurationMinutes: slotDurZ,
    contactName: z.string().min(1, "ກະລຸນາໃສ່ຊື່"),
    contactPhone: z.string().min(1, "ກະລຸນາໃສ່ເບີໂທ"),
    title: z.string().optional(),
    totalAmount: z.number().int().min(0, "ຍອດລວມຕ້ອງບໍ່ຕິດລົບ"),
    amountPaidNow: z.number().int().min(0, "ຈ່າຍຕອນນີ້ຕ້ອງບໍ່ຕິດລົບ"),
    pricingNotes: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!isCanonicalQuarterHhMm(data.startTime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເວລາເລີ່ມຕົ້ນຕ້ອງເປັນນາທີ 00 / 15 / 30 / 45",
        path: ["startTime"],
      });
    }
  })
  .refine((data) => data.amountPaidNow <= data.totalAmount, {
    message: "ຈ່າຍຕອນນີ້ຕ້ອງບໍ່ເກີນຍອດລວມ",
    path: ["amountPaidNow"],
  });

type PublicRoomBookingFormValues = z.infer<typeof PublicRoomBookingSchema>;

export type PublicRoomBookingFormProps = {
  slot: BookingSlot | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function PublicRoomBookingForm({
  slot,
  open,
  onOpenChange,
  onSuccess,
}: PublicRoomBookingFormProps) {
  const [error, setError] = useState<string | null>(null);

  const methods = RHF.useForm<PublicRoomBookingFormValues>({
    resolver: zodResolver(PublicRoomBookingSchema),
    defaultValues: {
      startTime: "09:00",
      slotDurationMinutes: 60 as PrivateLessonSlotDurationMinutes,
      contactName: "",
      contactPhone: "",
      title: "",
      totalAmount: 0,
      amountPaidNow: 0,
      pricingNotes: "",
    },
  });

  useEffect(() => {
    if (slot) {
      methods.reset({
        startTime: toQuarterHhMmInitial(slot.startTime),
        slotDurationMinutes: inferSlotDurationMinutesFromRange(
          slot.startTime,
          slot.endTime,
        ),
        contactName: "",
        contactPhone: "",
        title: "",
        totalAmount: 0,
        amountPaidNow: 0,
        pricingNotes: "",
      });
    }
  }, [slot, methods.reset]); // reset when slot changes

  const startTime = methods.watch("startTime");
  const slotDurationMinutes = methods.watch("slotDurationMinutes");
  const computedEndTime = useMemo(() => {
    if (!startTime?.trim()) return "";
    if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(slotDurationMinutes)) return "";
    try {
      return addMinutesToTimeHhMm(startTime, slotDurationMinutes);
    } catch {
      return "";
    }
  }, [startTime, slotDurationMinutes]);

  const totalAmountW = methods.watch("totalAmount");
  const amountPaidNowW = methods.watch("amountPaidNow");
  const pricingSummary = useMemo(() => {
    const total = Number(totalAmountW) || 0;
    const paid = Number(amountPaidNowW) || 0;
    const remaining = Math.max(0, total - paid);
    return { total, paid, remaining };
  }, [totalAmountW, amountPaidNowW]);

  const handleSubmit = async (vals: PublicRoomBookingFormValues) => {
    if (!slot) return;
    setError(null);
    try {
      const start = toTimeHHmmss(vals.startTime);
      const end = toTimeHHmmss(
        addMinutesToTimeHhMm(vals.startTime, vals.slotDurationMinutes),
      );
      await publicBookingApi.createRoomBooking({
        roomId: slot.roomId,
        date: slot.date,
        startTime: start,
        endTime: end,
        contactName: vals.contactName,
        contactPhone: vals.contactPhone,
        title: vals.title || undefined,
        totalAmount: vals.totalAmount,
        amountPaidNow: vals.amountPaidNow,
        pricingNotes: vals.pricingNotes?.trim() || undefined,
      });
      methods.reset();
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : "ຜິດພາດການຈອງ");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>ຈອງຫ້ອງ</DialogTitle>
        </DialogHeader>
        {slot && (
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
            <div className="font-medium">{slot.roomName}</div>
            <div className="text-muted-foreground">
              {format(new Date(slot.date), "d MMM yyyy")}
            </div>
          </div>
        )}
        <FormRoot methods={methods} onSubmit={handleSubmit}>
          <div className="flex flex-col gap-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <div className="font-medium text-sm leading-none">
                  ເວລາເລີ່ມຕົ້ນ
                </div>
                <RHF.Controller
                  name="startTime"
                  control={methods.control}
                  render={({ field }) => (
                    <QuarterHourMinuteSelect
                      value={field.value ?? "09:00"}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <div className="font-medium text-sm leading-none">
                  ຄວາມຍາວ (ນາທີ)
                </div>
                <RHF.Controller
                  name="slotDurationMinutes"
                  control={methods.control}
                  render={({ field }) => {
                    const raw = Number(field.value);
                    const safe = PRIVATE_LESSON_SLOT_DURATION_SET.has(raw)
                      ? raw
                      : 60;
                    return (
                      <SimpleSelect
                        value={String(safe)}
                        onValueChange={(val) => {
                          const m = Number(val);
                          if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(m)) return;
                          field.onChange(m);
                        }}
                        options={PRIVATE_LESSON_DURATION_SELECT_OPTIONS}
                        className="h-9 w-full"
                        placeholder="ເລືອກຄວາມຍາວ"
                      />
                    );
                  }}
                />
              </div>
              <div className="space-y-2">
                <div className="font-medium text-muted-foreground text-sm leading-none">
                  ເວລາສິ້ນສຸດ <span className="text-xs">(ຄຳນວນ)</span>
                </div>
                <div className="flex h-9 w-full items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
                  {computedEndTime || "—"}
                </div>
              </div>
            </div>
            <FormInput
              name="contactName"
              label="ຊື່ຜູ້ຈອງ"
              placeholder="ກະລຸນາໃສ່ຊື່"
            />
            <FormInput
              name="contactPhone"
              label="ເບີໂທ"
              placeholder="ກະລຸນາໃສ່ເບີໂທ"
            />
            <FormInput
              name="title"
              label="ຫົວຂໍ້ (ທາງເລືອກ)"
              placeholder="ຫົວຂໍ້ການຈອງ"
            />
            <div className="space-y-3 rounded-md border border-border bg-muted/20 px-3 py-3">
              <div className="font-medium text-sm">ຍອດເງິນ (ກີບ)</div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormNumber
                  name="totalAmount"
                  label="ຍອດລວມທີ່ຕົກລົງ"
                  min={0}
                  step={1}
                  placeholder="0"
                />
                <FormNumber
                  name="amountPaidNow"
                  label="ຈ່າຍຕອນນີ້"
                  min={0}
                  step={1}
                  placeholder="0"
                />
              </div>
              <FormTextarea
                name="pricingNotes"
                label="ໝາຍເຫດການຄິດລາຄາ (ທາງເລືອກ)"
                placeholder="ຕົກລົງກັບລູກຄ້າແນວໃດ"
                rows={2}
              />
              <div className="space-y-0.5 text-muted-foreground text-xs">
                <div>
                  ຍອດຄ້າງ:{" "}
                  <span className="font-medium text-foreground tabular-nums">
                    {pricingSummary.remaining.toLocaleString()} ກີບ
                  </span>
                </div>
                {pricingSummary.total > 0 && pricingSummary.remaining > 0 && (
                  <div>ຍອດຄ້າງຈະບັນທຶກເປັນໃບເກັບເງິນ — ຊຳລະຕໍ່ພາຍຫຼັງໄດ້.</div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                ຍົກເລີກ
              </Button>
              <Button isLoading={methods.formState.isSubmitting} type="submit">
                ຢືນຢັນການຈອງ
              </Button>
            </div>
          </div>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
